import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TituloAdmin from '@components/TituloAdmin';
import Boton from '@components/Boton';
import BotonIconoTabla from '@components/BotonIconoTabla';
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import { tablaEstylos, tablaTheadEstylos, trStyle, thStyle, tdStyle } from '@styles/tablaEstylos';
import { colors } from '@styles/colors';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import '@styles/modernTable.css';
import { getAllEmpresasApi, updateEmpresaApi } from '@api/wp/empresas/empresasApi';

function Empresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const cargarEmpresas = async () => {
    try {
      setCargando(true);
      setError('');
      const resp = await getAllEmpresasApi();
      const lista = resp?.result || resp?.data || [];
      setEmpresas(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error('Error al cargar empresas:', err);
      setError('No se pudieron cargar las empresas');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarEmpresas();
  }, []);

  const cambiarValor = (empresaId, campo, valor) => {
    setEmpresas((prev) =>
      prev.map((e) => (String(e.id) === String(empresaId) ? { ...e, [campo]: valor } : e))
    );
  };

  const guardarFila = async (empresaId) => {
    try {
      setProcesando(true);
      setError('');
      setMensaje('');
      const fila = empresas.find((e) => String(e.id) === String(empresaId));
      const id = fila?.id;
      if (!id) {
        setError('No se encontró el identificador de la empresa');
        return;
      }

      const payload = {
        key_bold_prod: fila?.key_bold_prod || '',
        clave_bold_prod: fila?.clave_bold_prod || '',
        key_bold_dev: fila?.key_bold_dev || '',
        clave_bold_dev: fila?.clave_bold_dev || ''
      };
      console.log('Payload a actualizar:', payload);
      await updateEmpresaApi(id, payload);
      setMensaje('Empresa actualizada correctamente');
    } catch (err) {
      console.error('Error al actualizar empresa:', err);
      setError('Error al actualizar empresa');
    } finally {
      setProcesando(false);
    }
  };

  // Filtrado por texto (nombre de empresa y campos Bold)
  const normalizar = (v) => (v || '').toString().toLowerCase();
  const empresasFiltradas = empresas.filter((emp) => {
    const q = normalizar(busqueda);
    if (!q) return true;
    return (
      normalizar(emp.nombre_empresa).includes(q) ||
      normalizar(emp.key_bold_prod).includes(q) ||
      normalizar(emp.clave_bold_prod).includes(q) ||
      normalizar(emp.key_bold_dev).includes(q) ||
      normalizar(emp.clave_bold_dev).includes(q)
    );
  });

  const descargarExcel = () => {
    const dataParaExcel = empresasFiltradas.map(e => ({
      'Nombre Empresa': e.nombre_empresa,
      'Key Bold Prod': e.key_bold_prod,
      'Clave Bold Prod': e.clave_bold_prod,
      'Key Bold Dev': e.key_bold_dev,
      'Clave Bold Dev': e.clave_bold_dev,
    }));

    const csv = [
      Object.keys(dataParaExcel[0]).join(','),
      ...dataParaExcel.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'empresas.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div id="cajaPrincipal" style={cajaPrincipalStyle}>
      <LoadingOverlay visible={procesando} />
      <TituloAdmin
        titulo="Pasarela de pagos"
        imagen_titulo="servicios"
        subtitulo="Listado y edición de credenciales Bold por empresas"
      />

      <div style={cajaContenedorAdminStyle}>
        {error && (
          <div style={{
            padding: '1.5vh 2vh',
            marginBottom: '2vh',
            borderRadius: '0.8vh',
            fontSize: '1.4vh',
            fontWeight: '500',
            fontFamily: 'Inter',
            border: `1px solid ${colors.errorText}`,
            backgroundColor: colors.errorShadow,
            color: colors.errorText
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {mensaje && (
          <div style={{
            padding: '1.5vh 2vh',
            marginBottom: '2vh',
            borderRadius: '0.8vh',
            fontSize: '1.4vh',
            fontWeight: '500',
            fontFamily: 'Inter',
            border: `1px solid ${colors.primary}`,
            backgroundColor: colors.resumenBg,
            color: colors.primaryText
          }}>
            {mensaje}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2vh', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1vh', alignItems: 'center' }}>
            <label style={{ fontFamily: 'Inter', fontSize: '1.5vh', color: colors.gray700 }}>Buscar:</label>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Filtrar por..."
              style={{ width: '40vh', padding: '1.0vh', borderRadius: '0.6vh', border: `2px solid ${colors.gray200}`, fontSize: '1.6vh', backgroundColor: colors.white, outline: 'none', fontFamily: 'Inter' }}
            />
            <Boton label="Limpiar" onClick={() => setBusqueda('')} color={colors.secondaryButton} ancho="14vh" alto="4.5vh" />
          </div>
        </div>

        {/* Tabla de empresas */}
        <div className="modern-table-container">
          <table id="tabla-empresas" className="modern-table">
            <thead>
              <tr>
                <th>Nombre Empresa</th>
                <th>Key Bold Prod</th>
                <th>Clave Bold Prod</th>
                <th>Key Bold Dev</th>
                <th>Clave Bold Dev</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr>
                  <td colSpan={6}><LabelCarga nombre="empresas" /></td>
                </tr>
              ) : empresasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2vh', color: colors.secondaryButton }}>No hay empresas registradas</td>
                </tr>
              ) : (
                empresasFiltradas.map((emp, idx) => (
                  <tr key={emp.id || idx}>
                    <td>{emp.nombre_empresa || ''}</td>
                  <td>
                    <input
                      type="text"
                      value={emp.key_bold_prod || ''}
                      onChange={(e) => cambiarValor(emp.id, 'key_bold_prod', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '1.0vh',
                        borderRadius: '0.6vh',
                        border: `2px solid ${colors.gray200}`,
                        fontSize: '1.6vh',
                        backgroundColor: colors.white,
                        outline: 'none',
                        fontFamily: 'Inter'
                      }}
                      disabled={procesando}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={emp.clave_bold_prod || ''}
                      onChange={(e) => cambiarValor(emp.id, 'clave_bold_prod', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '1.0vh',
                        borderRadius: '0.6vh',
                        border: `2px solid ${colors.gray200}`,
                        fontSize: '1.6vh',
                        backgroundColor: colors.white,
                        outline: 'none',
                        fontFamily: 'Inter'
                      }}
                      disabled={procesando}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={emp.key_bold_dev || ''}
                      onChange={(e) => cambiarValor(emp.id, 'key_bold_dev', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '1.0vh',
                        borderRadius: '0.6vh',
                        border: `2px solid ${colors.gray200}`,
                        fontSize: '1.6vh',
                        backgroundColor: colors.white,
                        outline: 'none',
                        fontFamily: 'Inter'
                      }}
                      disabled={procesando}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={emp.clave_bold_dev || ''}
                      onChange={(e) => cambiarValor(emp.id, 'clave_bold_dev', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '1.0vh',
                        borderRadius: '0.6vh',
                        border: `2px solid ${colors.gray200}`,
                        fontSize: '1.6vh',
                        backgroundColor: colors.white,
                        outline: 'none',
                        fontFamily: 'Inter'
                      }}
                      disabled={procesando}
                    />
                  </td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <BotonIconoTabla
                          icono="save"
                          tooltipText={procesando ? 'Guardando...' : 'Guardar'}
                          onClick={() => guardarFila(emp.id)}
                          disabled={procesando}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {/* Contenedor para ubicar botones de DataTables debajo de la tabla */}

        </div>
      </div>
    </div>
  );
}

export default Empresas;